<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1>{{ $t('common.product') }}</h1>
      <div class="details-component__header_right"
        v-if="!isDeleted() && isLandlord() && $route.params.id && !product.sharedBy">
        <router-link :to="{ name: 'report-conference-product', params: { id: selectedProductId } }" class="link"
          v-if="selectedProductId && isConferenceParking()">
          {{ $t('conferencereport.generate_report') }}
          <i class="icon icon--rows"></i>
        </router-link>
        <router-link :to="{ name: 'edit-product', params: { id: selectedProductId } }" class="link">
          {{ $t('common.change') }}&nbsp;<i class="icon icon--arrow"></i>
        </router-link>
        <a v-on:click="showDeletingConfirmation" class="link link--orange">{{ $t('common.delete') }}&nbsp;<i
            class="icon icon--cross"></i></a>
      </div>
    </div>

    <div class="details-component__content content">
      <div class="content__cell">
        <table class="details-table no-col-padding fixed-table label-col-wide-150">
          <tr class="details-table__row" v-if="isSuperAdmin()">
            <td class="details-table__col details-table__col_label">{{ $t('common.landlord') }}</td>
            <td class="details-table__col landlord__name" v-if="product.partner">{{ product.partner.name }}</td>
          </tr>
          <tr class="details-table__row">
            <td class="details-table__col details-table__col_label">{{ $t('common.name') }}</td>
            <td class="details-table__col bold">{{ product.name }}</td>
          </tr>
          <tr v-if="product.sharedBy" class="details-table__row">
            <td class="details-table__col details-table__col_label">{{ $t('common.shared_by') }}</td>
            <td class="details-table__col bold">{{ product.sharedBy.name }}</td>
          </tr>
          <tr class="details-table__row" v-if="product.type">
            <td class="details-table__col details-table__col_label">{{ $t('products.type') }}</td>
            <td class="details-table__col">{{ $t('product.' + product.type) }}</td>
          </tr>
          <tr class="details-table__row" v-if="product.dateExpiration">
            <td class="details-table__col details-table__col_label">{{ $t('products.expiration_date') }}</td>
            <td class="details-table__col">{{ getDateExpiration }}</td>
          </tr>
          <tr class="details-table__row" v-if="product.bookable_parking_spots_count">
            <td class="details-table__col details-table__col_label">{{ $t('products.bookable_parking_spots_count') }}
            </td>
            <td class="details-table__col">{{ product.bookable_parking_spots_count }}</td>
          </tr>
          <template v-for="zone in product.zones">
            <tr :key="zone.id" class="details-table__row">
              <td class="details-table__col details-table__col_label">{{ $t('common.zone') }}</td>
              <td class="details-table__col">{{ `${zone.code} (${zone.address})` }}</td>
            </tr>
          </template>
          <tr class="details-table__row" v-if="isPermitPack() && (!product.canExceedCapacity || isTenant())">
            <td class="details-table__col details-table__col_label">{{ $t('products.parking_spots') }}</td>
            <td class="details-table__col">{{ product.total - product.issued }}</td>
          </tr>
          <tr class="details-table__row" v-if="isPermitPack()">
            <td class="details-table__col details-table__col_label">{{ $t('common.issued') }}</td>
            <td class="details-table__col">{{ product.issued }}</td>
          </tr>
          <tr class="details-table__row" v-if="isPermitPack() && product.permanentSpotNumber">
            <td class="details-table__col details-table__col_label">{{ $t('products.parking_spot_number') }}</td>
            <td class="details-table__col">{{ product.permanentSpotNumber }}</td>
          </tr>

          <!-- virtual spots field?-->
          <tr class="details-table__row" v-if="showVirtual">
            <td class="details-table__col details-table__col_label">{{ $t('products.virtual_spots') }}</td>
            <td class="details-table__col">{{ product.issued - product.total }}</td>
          </tr>
          <tr class="details-table__row" v-if="product.commission && isGuestParking() && isLandlord()">
            <td class="details-table__col details-table__col_label">{{ $t('products.commission') }}</td>
            <td class="details-table__col">{{ product.commission.value }} {{ product.commission.type }}</td>
          </tr>
          <tr class="details-table__row" v-if="product.price && isPermitPack() && isLandlord()">
            <td class="details-table__col details-table__col_label">{{ $t('products.price') }}</td>
            <td class="details-table__col">{{ product.price }} {{ currency_short }}</td>
          </tr>
          <tr class="details-table__row" v-if="product.comment && isLandlord()">
            <td class="details-table__col details-table__col_label">{{ $t('products.comment') }}</td>
            <td class="details-table__col">{{ product.comment }}</td>
          </tr>
          <tr class="details-table__row" v-if="product.cost_center && isLandlord() && user.paymentModuleEnabled">
            <td class="details-table__col details-table__col_label">{{ $t('parking.cost_center') }}</td>
            <td class="details-table__col">{{ product.cost_center }}</td>
          </tr>
          <tr class="details-table__row" v-if="product.perDay && isGuestParking()">
            <td class="details-table__col details-table__col_label">{{ $t('products.per_day') }}</td>
            <td class="details-table__col">{{ product.perDay }}</td>
          </tr>
          <tr class="details-table__row" v-if="product.frequency && isGuestParking()">
            <td class="details-table__col details-table__col_label">{{ $t('products.frequency') }}</td>
            <td class="details-table__col">{{ getFrequency() }}</td>
          </tr>
          <tr class="details-table__row" v-if="product.concurrentParkings && isGuestParking()">
            <td class="details-table__col details-table__col_label">{{ $t('products.concurrent_parkings') }}</td>
            <td class="details-table__col">{{ product.concurrentParkings }}</td>
          </tr>
          <tr class="details-table__row" v-if="isGuestParking()">
            <td class="details-table__col details-table__col_label">{{ $t('add_product.time_limit') }}</td>
            <td class="details-table__col">{{ timeToText }}</td>
          </tr>
          <tr class="details-table__row" v-if="isGuestParking()">
            <td class="details-table__col details-table__col_label">{{ $t('products.payment_grace_period') }}</td>
            <td class="details-table__col">{{ product.payment_grace_period }}</td>
          </tr>
          <tr class="details-table__row auto-height" v-if="isPermitPack() && product.canExceedCapacity === true">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="can_exceed_capacity" name="can_exceed_capacity"
                  class="form__fieldset__checkbox" v-model="product.canExceedCapacity">
                <span class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('add_product.enable_virtual_parking_spots') }}</span>
              </label>
            </td>
          </tr>
          <tr class="details-table__row auto-height" v-if="isPermitPack() && product.canUseCards === true">
            <td class="details-table__col txt-to-left padding-t-4 always-cell" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="can_use_cards" name="canUseCards" disabled class="form__fieldset__checkbox"
                  v-model="product.canUseCards">
                <span class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('add_product.allow_to_use_cards') }}</span>
              </label>
            </td>
          </tr>
          <tr class="details-table__row auto-height"
            v-if="isPermitPack() && product.tenantCanManagePermitStart === true">
            <td class="details-table__col txt-to-left padding-t-4 always-cell" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="tenant_can_manage_permit_start" name="tenantCanManagePermitStart" disabled
                  class="form__fieldset__checkbox" v-model="product.tenantCanManagePermitStart">
                <span class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('add_product.allow_tenant_to_manage_permit_start') }}</span>
              </label>
            </td>
          </tr>
          <tr class="details-table__row auto-height"
            v-if="isPermitPack() && product.tenantCanManagePermitExpiration === true">
            <td class="details-table__col txt-to-left padding-t-4 always-cell" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="tenant_can_manage_permit_expiration" name="tenantCanManagePermitExpiration"
                  disabled class="form__fieldset__checkbox" v-model="product.tenantCanManagePermitExpiration">
                <span class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('add_product.allow_tenant_to_manage_permit_expiration')
                  }}</span>
              </label>
            </td>
          </tr>
          <tr class="details-table__row auto-height"
            v-if="isPermitPack() && product.tenantCanManagePermitSpot === true">
            <td class="details-table__col txt-to-left padding-t-4 always-cell" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="tenant_can_manage_permit_expiration" name="tenantCanManagePermitSpot"
                  disabled class="form__fieldset__checkbox" v-model="product.tenantCanManagePermitSpot">
                <span class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('add_product.allow_tenant_to_manage_spot') }}</span>
              </label>
            </td>
          </tr>
          <tr class="details-table__row auto-height"
            v-if="isPermitPack() && product.auto_start_enabled === true">
            <td class="details-table__col txt-to-left padding-t-4 always-cell" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="auto_start_enabled" name="auto_start_enabled" disabled
                  class="form__fieldset__checkbox" v-model="product.auto_start_enabled">
                <span class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('add_product.auto_start_enabled') }}</span>
              </label>
            </td>
          </tr>
          <tr class="details-table__row auto-height"
            v-if="isGuestParking() || isEasyParking() && product.isUseSharedSession === true">
            <td class="details-table__col txt-to-left padding-t-4 always-cell" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="tenant_can_manage_permit_expiration" name="isUseSharedSession" disabled
                  class="form__fieldset__checkbox" v-model="product.isUseSharedSession">
                <span class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('add_product.shared_product_session') }}</span>
              </label>
            </td>
          </tr>
          <tr class="details-table__row auto-height"
            v-if="isGuestParking() || isEasyParking() && product.is_time_limit_edit === true">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="is_time_limit_edit" name="is_time_limit_edit" disabled
                  class="form__fieldset__checkbox" v-model="product.is_time_limit_edit">
                <span class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('parking.time_limit_edite') }}</span>
              </label>
            </td>
          </tr>
          <tr class="details-table__row auto-height"
            v-if="isGuestParking() || isEasyParking() && product.is_required_comment === true">
            <td class="details-table__col txt-to-left padding-t-4  always-cell" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="is_required_comment" name="isRequiredComment" disabled
                  class="form__fieldset__checkbox" v-model="product.is_required_comment">
                <span class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('add_product.is_required_comment') }}</span>
              </label>
            </td>
          </tr>
          <tr class="details-table__row auto-height"
            v-if="isGuestParking() || isEasyParking() && product.is_allow_guest_parking === true">
            <td class="details-table__col txt-to-left padding-t-4  always-cell" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="is_allow_guest_parking" name="isRequiredComment" disabled
                  class="form__fieldset__checkbox" v-model="product.is_allow_guest_parking">
                <span class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('add_product.is_allow_guest_parking') }}</span>
              </label>
            </td>
          </tr>
          <tr class="details-table__row auto-height"
            v-if="isGuestParking() || isEasyParking() && product.payment_required === true">
            <td class="details-table__col txt-to-left padding-t-4  always-cell" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="payment_required" name="payment_required" disabled
                  class="form__fieldset__checkbox" v-model="product.payment_required">
                <span class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('add_product.is_paid_guest_parking') }}</span>
              </label>
            </td>
          </tr>
          <tr class="details-table__row auto-height"
            v-if="isGuestParking() || isEasyParking() && product.allow_end_time_override === true">
            <td class="details-table__col txt-to-left padding-t-4  always-cell" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="allow_end_time_override" name="allow_end_time_override" disabled
                  class="form__fieldset__checkbox" v-model="product.allow_end_time_override">
                <span class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('add_product.allow_end_time_override') }}</span>
              </label>
            </td>
          </tr>
          <tr class="details-table__row auto-height"
            v-if="isGuestParking() || isEasyParking()">
            <td class="details-table__col txt-to-left padding-t-4  always-cell" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="allow_past_start" name="allow_past_start" disabled
                  class="form__fieldset__checkbox" v-model="product.allow_past_start">
                <span class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('add_product.allow_past_start') }}</span>
              </label>
            </td>
          </tr>
          <tr class="details-table__row auto-height" v-if="isGuestParking() && user.autopayEnabled">
            <td class="details-table__col txt-to-left padding-t-4  always-cell" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="autopay_enabled" name="autopay_enabled" disabled
                  class="form__fieldset__checkbox" v-model="product.autopay_enabled">
                <span class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('autopay.autopay_enabled') }}</span>
              </label>
            </td>
          </tr>
          <tr class="details-table__row auto-height" v-if="isEasyParking() && product.requires_confirmation === true">
            <td class="details-table__col txt-to-left padding-t-4  always-cell" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="requires_confirmation" name="requires_confirmation" disabled
                  class="form__fieldset__checkbox" v-model="product.requires_confirmation">
                <span class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('add_product.confirm_all_sessions') }}</span>
              </label>
            </td>
          </tr>
          <tr class="details-table__row" v-if="isConferenceParking()">
            <td class="details-table__col details-table__col_label">{{ $t('conference.parking_spot_count') }}</td>
            <td class="details-table__col">{{ product.total }}</td>
          </tr>
          <tr class="details-table__row" v-if="isConferenceParking()">
            <td class="details-table__col details-table__col_label">{{ $t('conference.registered_count') }}</td>
            <td class="details-table__col">{{ product.conferenceParkings.length }}</td>
          </tr>
          <tr class="details-table__row" v-if="isConferenceParking()">
            <td class="details-table__col details-table__col_label">{{ $t('conference.price_per_vehicle') }}</td>
            <td class="details-table__col">{{ product.price }}</td>
          </tr>
          <tr class="details-table__row" v-if="isConferenceParking()">
            <td class="details-table__col details-table__col_label">{{ $t('conference.conference_url') }}</td>
            <router-link :to="{ name: 'conference-parking', params: { url: product.conferenceUrl } }" class="link">
              {{ `${conferenceBaseUrl}${product.conferenceUrl}` }}
            </router-link>
          </tr>
        </table>
      </div>
      <div id='parking-map-dom' style='margin-top: 30px; width: 100%; height: 300px;'></div>
    </div>

    <ConfirmationModal v-if="showConfirmationModal" @cancel="cancelProductDelete" @confirmed="deleteProduct">
      <h1 slot="header">{{ $t('common.deleting') }}: {{ productName }} </h1>
      <p slot="body">{{ $t('products.confirm_delete') }}</p>
    </ConfirmationModal>

    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">{{ productWarningHeader }}</h1>
      <p slot="body">{{ productWarningBody }}</p>
    </WarningModal>
  </div>
</template>

<script>
import Vue from 'vue';
import L from 'leaflet';
import { sha256 } from 'js-sha256';
import { mapGetters } from 'vuex';
import * as mTypes from 'store/mutation-types';
import ConfirmationModal from 'common/ConfirmationModal';
import WarningModal from 'common/WarningModal';
import moment from 'moment';
import InputBoxes from 'common/InputBoxes';

export default {
  props: ['product', 'time_limit'],
  components: {
    ConfirmationModal,
    WarningModal,
    InputBoxes,
  },
  computed: {
    getDateExpiration() {
      return moment(this.product.dateExpiration).format('DD.MM.YYYY');
    },
    showMap() {
      return this.coordinates && this.coordinates.length > 0;
    },
    coordinates() {
      return this.$store.getters[`${this.role()}/product/coordinates`];
    },
    showVirtual() {
      return this.isLandlord()
        && this.product.canExceedCapacity
        && this.product.issued > this.product.total
        && this.product.type === 'permitPack';
    },
    ...mapGetters({
      selectedProductId: 'products/product/selectedProductId',
      productName: 'products/product/name',
      productError: 'products/product/error',
      user: 'user/user/user',
    }),
    timeToText() {
      const TIME = this.time_limit;
      let days = 0;
      let hours = 0;
      let minutes = 0;
      if (TIME) {
        days = Math.floor(TIME / 86400);
        hours = Math.floor((TIME % 86400) / 3600);
        minutes = Math.floor((TIME % 3600) / 60);
      }

      if (days > 0) {
        return `${days} ${this.$tc('pricingrules.day', days)} ${hours} ${this.$t('pricingrules.hour')} ${minutes} ${this.$t('pricingrules.minute')}`;
      } else if (hours > 0 && days === 0) {
        return `${hours} ${this.$t('pricingrules.hour')} ${minutes} ${this.$t('pricingrules.minute')}`;
      } else if (hours === 0 && days === 0) {
        return `${minutes} ${this.$t('pricingrules.minute')}`;
      }
      return `${minutes} ${this.$t('pricingrules.minute')}`;
    },
  },
  watch: {
    $route: 'fetchProduct',
  },
  route: {
    canReuse: true,
  },
  created() {
    if (!this.user.authenticated) {
      return;
    }
    this.fetchProduct();
    this.initMap();
  },
  mounted() {
    this.updateMap();
  },
  methods: {
    initMap() {
      Vue.nextTick(() => {
        if (!this.mapInstance) {
          this.mapInstance = new mapboxgl.Map({
            container: 'parking-map-dom',
            style: `${process.env.MAPS_URL}/styles/${this.style}/style.json`,
            hash: false,
            center: this.center,
            zoom: this.zoom,
          });
          this.mapInstance.addControl(new mapboxgl.NavigationControl());
        }
      });
    },
    isDeleted() {
      this.$emit('isDeleted', this.product.isDeleted);
      return this.product.isDeleted;
    },
    fetchProduct() {
      if (this.$route.params.id) {
        this.$store.dispatch(`${this.role()}/product/fetch`, { id: this.$route.params.id, user: this.user }).then(() => {
          this.updateMap();
        });
      } else {
        this.$store.commit(`${this.role()}/product/${mTypes.CLEAR_STATE}`);
      }
    },
    cleanupCoordinates(coordinates) {
      let data = {};
      if (coordinates) {
        if (Array.isArray(coordinates) && coordinates[0] && coordinates[0].type === 'Feature') {
          data = {
            type: 'FeatureCollection',
            features: coordinates,
          };
        } else if (coordinates === Object(coordinates) && coordinates.type === 'Feature') {
          data = {
            type: 'FeatureCollection',
            features: [coordinates],
          };
        } else if (Array.isArray(coordinates) && coordinates[0] && coordinates[0].type === 'FeatureCollection') {
          const features = [];
          // eslint-disable-next-line
          for (const c of coordinates) {
            // eslint-disable-next-line
            for (const f of c.features) {
              features.push(f);
            }
          }
          data = {
            type: 'FeatureCollection',
            features,
          };
        } else if (coordinates === Object(coordinates) && coordinates.type === 'FeatureCollection') {
          data = coordinates;
        }
      }
      return data;
    },
    addSourceNLayers() {
      const id = sha256(JSON.stringify(this.coordinates));
      const data = this.cleanupCoordinates(this.coordinates, id);

      // Create source if not exists
      const source = this.mapInstance.getSource('zone-products-data');
      if (!source) {
        this.mapInstance.addSource('zone-products-data', {
          type: 'geojson',
          data,
        });
      } else {
        source.setData(data);
      }

      // Create layer if not exists
      if (!this.mapInstance.getLayer('zone-outline-products')) {
        this.mapInstance.addLayer({
          id: 'zone-outline-products',
          type: 'fill',
          source: 'zone-products-data',
          layout: {},
          paint: {
            'fill-color': '#088',
            'fill-opacity': 0.8,
          },
        });
      }
    },
    updateMap() {
      if (this.mapInstance && this.coordinates && this.coordinates.length > 0) {
        // Set center
        const bounds = L.geoJSON(this.coordinates).getBounds();
        if (bounds.isValid()) {
          const sw = bounds.getSouthWest();
          const ne = bounds.getNorthEast();
          const llb = new mapboxgl.LngLatBounds(sw, ne);
          this.mapInstance.fitBounds(llb, { padding: 20 });
        }
        Vue.nextTick(() => {
          if (this.coordinates && this.coordinates.length > 0) {
            if (this.mapInstance.isStyleLoaded()) {
              this.addSourceNLayers();
            } else {
              this.mapInstance.on('load', () => {
                this.addSourceNLayers();
              });
            }
          }
        });
      }
    },
    showDeletingConfirmation() {
      let cannotDelete = false;
      if (this.product.issued > 0) {
        cannotDelete = true;
      }
      if (cannotDelete) {
        this.productWarningHeader = `${this.$t('common.deleting')}:  ${this.productName}`;
        this.productWarningBody = `${this.$t('common.issued_permits')} ${this.$t('products.cannot_delete')}`;
        this.showWarningModal = true;
      } else {
        this.showConfirmationModal = true;
      }
    },
    closeWarningModal() {
      this.showWarningModal = false;
      this.productWarningBody = null;
      this.productWarningHeader = null;
    },
    cancelProductDelete() {
      this.showConfirmationModal = false;
    },
    deleteProduct() {
      this.showConfirmationModal = false;
      this.$store.dispatch('products/product/delete', { id: this.selectedProductId, user: this.user })
        .then(() => {
          if (this.productError) {
            this.productWarningHeader = `${this.$t('common.deleting')}:  ${this.productName}`;
            const error = this.productError.body.error;
            if (error === 'product_has_related_entities') {
              this.productWarningBody = this.$t('products.product_has_related_entities');
            } else {
              this.productWarningBody = this.$t('products.delete_error');
            }
            this.showWarningModal = true;
          } else {
            this.$store.commit(`landlord/products/${mTypes.REMOVE_DATA}`, this.selectedProductId);
            this.$router.push({ name: 'products' });
          }
        })
        .catch(() => {
        });
    },

    role() {
      return this.isLandlord() ? 'products' : 'tenants';
    },
    getFrequency() {
      return this.steps.find(i => i.key === this.product.frequency).value;
    },
    isPermitPack() {
      return this.product.type === 'permitPack';
    },
    isGuestParking() {
      return this.product.type === 'guestParking';
    },
    isConferenceParking() {
      return this.product.type === 'conferenceParking';
    },
    isEasyParking() {
      return this.product.type === 'easyParking';
    },

  },

  data() {
    return {
      currency_short: process.env.CURRENCY_SHORT,
      showConfirmationModal: false,
      showWarningModal: false,
      productWarningBody: null,
      productWarningHeader: null,
      zoom: 15,
      center: [24.707, 59.412],
      style: 'klokantech-basic',
      conferenceBaseUrl: `${process.env.BASE_URL}conference/`,
      mapInstance: null,
      steps: [
        { key: -1440, value: `-24 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -720, value: `-12 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -480, value: `-8 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -360, value: `-6 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -300, value: `-5 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -240, value: `-4 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -180, value: `-3 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -120, value: `-2 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -90, value: `-1 ${this.$t('pricingrules.hour')} 30 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: -60, value: `-1 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -45, value: `-45 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: -30, value: `-30 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: -15, value: `-15 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: -10, value: `-10 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: -5, value: `-5 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 0, value: '' },
        { key: 5, value: `5 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 10, value: `10 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 15, value: `15 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 30, value: `30 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 45, value: `45 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 60, value: `1 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 90, value: `1 ${this.$t('pricingrules.hour')} 30 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 120, value: `2 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 180, value: `3 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 240, value: `4 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 300, value: `5 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 360, value: `6 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 480, value: `8 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 720, value: `12 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 1440, value: `24 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
      ],
    };
  },
};
</script>
<style lang="scss" scoped>
#parking-map-dom {
  margin-top: 30px;
  width: 100%;
  height: 300px;
}



// WEBPACK FOOTER //
// src/components/products/ProductDetails.vue